"use client";

import {
  getAuthToken,
  useIsLoggedIn,
  useRefreshAuth,
} from "@dynamic-labs/sdk-react-core";
import { useEffect } from "react";

import { ONE_MINUTE_MS, ONE_SECOND_MS } from "#/config/time";
import { useTradingSession } from "#/services/kyan/hooks/useTradingSession";
import { Logger } from "#/utils/logger";

const REFRESH_BUFFER_MS = 30 * ONE_MINUTE_MS;
const REFRESH_RETRY_DELAY_MS = ONE_MINUTE_MS;

const expiresInMs = (): number | undefined => {
  const payload = getAuthToken()?.split(".")[1];

  if (!payload) return undefined;

  try {
    const { exp } = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as { exp?: number };

    return typeof exp === "number"
      ? exp * ONE_SECOND_MS - Date.now()
      : undefined;
  } catch {
    return undefined;
  }
};

export const AuthRefreshUpdater = () => {
  const isLoggedIn = useIsLoggedIn();
  const refreshAuth = useRefreshAuth();
  const { isTradingSessionEnabled } = useTradingSession();

  useEffect(() => {
    if (!isLoggedIn || !isTradingSessionEnabled) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let isDisposed = false;

    const scheduleRefresh = (minDelayMs = 0) => {
      const expiresIn = expiresInMs();

      if (expiresIn === undefined) return;

      const delay = Math.max(minDelayMs, expiresIn - REFRESH_BUFFER_MS);

      timeoutId = setTimeout(async () => {
        if (isDisposed) return;

        let isRefreshFailed = false;

        try {
          await refreshAuth();
          Logger.success(
            "Auth Refresh Updater:",
            "Token refreshed successfully",
          );
        } catch (error) {
          isRefreshFailed = true;
          Logger.error("Auth Refresh Updater:", "Token refresh failed", error);
        }

        if (isDisposed) return;

        scheduleRefresh(isRefreshFailed ? REFRESH_RETRY_DELAY_MS : 0);
      }, delay);
    };

    scheduleRefresh();

    return () => {
      isDisposed = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [isLoggedIn, isTradingSessionEnabled, refreshAuth]);

  return null;
};
